import "../foundation/ctl-button.js";
import "../foundation/ctl-link.js";
import "../foundation/ctl-badge.js";
import "../data-display/ctl-tag.js";
import "../foundation/ctl-typography.js";

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --font: var(--ctl-sf-font, var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial));
    --text: var(--ctl-sf-text, var(--ctl-color-text));
    --muted: var(--ctl-sf-muted, var(--ctl-color-muted));
    --bg: var(--ctl-sf-bg, var(--ctl-color-bg));
    --surface: var(--ctl-sf-surface, var(--ctl-color-surface));
    --border: var(--ctl-sf-border, var(--ctl-color-border));
    --brand: var(--ctl-sf-brand, var(--ctl-color-brand));
    --radius: var(--ctl-sf-radius, var(--ctl-radius-lg, 14px));
    --shadow: var(--ctl-sf-shadow, 0 6px 18px rgba(0,0,0,.10));
    --focus: var(--ctl-sf-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--font);
    color: var(--text);
  }

  .card{
    position: relative;
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    overflow:hidden;
    transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
  }

  :host([clickable]) .card{
    cursor: pointer;
  }
  :host([clickable]) .card:hover{
    transform: translateY(-1px);
    box-shadow: var(--ctl-shadow-float);
    border-color: color-mix(in srgb, var(--brand) 20%, var(--border));
  }
  :host([clickable]) .card:focus-within{
    box-shadow: var(--ctl-shadow-float), var(--focus);
  }

  :host([selected]) .card{
    border-color: color-mix(in srgb, var(--brand) 55%, var(--border));
    box-shadow: var(--ctl-shadow-float), 0 0 0 3px color-mix(in srgb, var(--ctl-color-brand) 10%, transparent);
  }

  :host([disabled]) .card{
    opacity: .65;
    filter: grayscale(.05);
    pointer-events: none;
  }

  .grid{
    display:grid;
    grid-template-columns: 160px 1fr;
    gap: var(--ctl-space-3-5);
    padding: var(--ctl-space-3-5);
    align-items: stretch;
  }

  .media{
    border-radius: var(--ctl-radius-12);
    border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
    background: color-mix(in srgb, var(--surface) 92%, #000 8%);
    overflow:hidden;
    display:flex;
    align-items:center;
    justify-content:center;
    min-height: 118px;
  }

  .media ::slotted(img){
    width: 100%;
    height: 100%;
    object-fit: cover;
    display:block;
  }

  .body{
    min-width: 0;
    display:grid;
    gap: var(--ctl-space-2-5);
    align-content: start;
  }

  .topRow{
    display:flex;
    align-items:flex-start;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
  }

  .titleWrap{
    min-width:0;
  }

  .title{
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing:-0.01em;
    line-height: 1.15;
    margin: 0;
    font-size: var(--ctl-font-size-lg);
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .subtitle{
    margin: var(--ctl-space-1) 0 0;
    color: var(--muted);
    font-size: var(--ctl-font-size-md);
    line-height: 1.35;
    display:flex;
    gap: var(--ctl-space-2);
    flex-wrap: wrap;
    align-items:center;
  }

  .badges{
    display:flex;
    gap: var(--ctl-space-2);
    align-items:center;
    flex-wrap: wrap;
    justify-content:flex-end;
  }

  .priceBlock{
    display:flex;
    align-items: baseline;
    gap: var(--ctl-space-2-5);
    flex-wrap: wrap;
  }
  .price{
    font-size: var(--ctl-font-size-xl);
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing: -0.02em;
    line-height: 1;
  }
  .per{
    font-size: var(--ctl-font-size-sm);
    color: var(--muted);
    font-weight: var(--ctl-font-weight-bold);
  }
  .orig{
    font-size: var(--ctl-font-size-md);
    color: var(--muted);
    text-decoration: line-through;
    font-weight: var(--ctl-font-weight-bold);
  }

  .promo{
    display:flex;
    gap: var(--ctl-space-2-5);
    flex-wrap: wrap;
    align-items:center;
    color: color-mix(in srgb, var(--brand) 70%, var(--text));
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-extrabold);
  }
  .promo:empty{ display:none; }

  .features{
    display:flex;
    gap: var(--ctl-space-2);
    flex-wrap: wrap;
    align-items:center;
  }
  .features:empty{ display:none; }

  .actions{
    display:flex;
    gap: var(--ctl-space-2-5);
    align-items:center;
    justify-content:flex-end;
    flex-wrap: wrap;
    margin-top: var(--ctl-space-0-5);
  }

  .footer{
    padding: 0 var(--ctl-space-3-5) var(--ctl-space-3-5);
    color: var(--muted);
    font-size: var(--ctl-font-size-sm);
  }
  .footer:empty{ display:none; }

  .divider{
    height: 1px;
    background: color-mix(in srgb, var(--border) 70%, transparent);
    margin: 0 var(--ctl-space-3-5);
  }

  /* Availability state */
  .sold{
    position:absolute;
    inset: 0;
    background: linear-gradient(0deg, var(--ctl-color-surface-wash), var(--ctl-color-surface-wash));
    display:none;
    align-items:center;
    justify-content:center;
    padding: var(--ctl-space-4);
    text-align:center;
  }
  :host([unavailable]) .sold{ display:flex; }
  .soldInner{
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: var(--ctl-radius-full);
    padding: var(--ctl-space-2-5) var(--ctl-space-3-5);
    font-weight: var(--ctl-font-weight-black);
    color: var(--muted);
  }

  /* Responsive */
  @media (max-width: 680px){
    .grid{
      grid-template-columns: 1fr;
    }
    .media{
      min-height: 140px;
    }
    .actions{
      justify-content: stretch;
    }
    .actions ctl-button{
      width: 100%;
    }
  }

  /* Hide internal helper templates if any */
  ::slotted(template){ display:none; }
</style>

<div class="card" part="card">
  <div class="grid" part="grid">
    <div class="media" part="media">
      <slot name="media">
        <div style="font-size:var(--ctl-font-size-sm); color: var(--muted); font-weight:800;">No image</div>
      </slot>
    </div>

    <div class="body" part="body">
      <div class="topRow" part="topRow">
        <div class="titleWrap">
          <div class="title" part="title" id="title"></div>
          <div class="subtitle" part="subtitle" id="subtitle"></div>
        </div>

        <div class="badges" part="badges">
          <slot name="badges"></slot>
        </div>
      </div>

      <div class="priceBlock" part="price">
        <div class="price" id="price"></div>
        <div class="per">/ month</div>
        <div class="orig" id="orig" hidden></div>
      </div>

      <div class="promo" part="promo">
        <slot name="promo"></slot>
      </div>

      <div class="features" part="features">
        <slot name="features"></slot>
      </div>

      <div class="actions" part="actions">
        <slot name="actions"></slot>
        <ctl-button id="selectBtn"></ctl-button>
      </div>
    </div>
  </div>

  <div class="divider" part="divider"></div>
  <div class="footer" part="footer">
    <slot name="footer"></slot>
  </div>

  <div class="sold" part="unavailableOverlay" aria-hidden="true">
    <div class="soldInner" part="unavailablePill">Currently unavailable</div>
  </div>
</div>
`;

function currency(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
  } catch {
    // fallback
    return `$${Math.round(n)}`;
  }
}
function clampStr(s){ return String(s ?? "").trim(); }

export class CtlUnitCard extends HTMLElement {
  static get observedAttributes() {
    return [
      "unit-id",
      "name",
      "size",
      "dims",
      "price",
      "original-price",
      "cta",
      "href",
      "selected",
      "unavailable",
      "disabled",
      "clickable"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._card = this.shadowRoot.querySelector(".card");
    this._title = this.shadowRoot.getElementById("title");
    this._subtitle = this.shadowRoot.getElementById("subtitle");
    this._price = this.shadowRoot.getElementById("price");
    this._orig = this.shadowRoot.getElementById("orig");
    this._selectBtn = this.shadowRoot.getElementById("selectBtn");

    this._onClick = this._onClick.bind(this);
    this._onSelect = this._onSelect.bind(this);
    this._onCardKeydown = this._onCardKeydown.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("cta")) this.setAttribute("cta", "Select unit");
    this._sync();

    // Primary button action
    this._selectBtn.addEventListener("click", this._onSelect);

    // Optional clickable card behavior
    this._card.addEventListener("click", this._onClick);
    this._card.setAttribute("tabindex", this.clickable ? "0" : "-1");
    this._card.addEventListener("keydown", this._onCardKeydown);
  }

  disconnectedCallback() {
    this._selectBtn.removeEventListener("click", this._onSelect);
    this._card.removeEventListener("click", this._onClick);
    this._card.removeEventListener("keydown", this._onCardKeydown);
  }

  attributeChangedCallback() {
    this._sync();
  }

  // ---- attrs ----
  get unitId(){ return this.getAttribute("unit-id") || null; }
  set unitId(v){ v == null ? this.removeAttribute("unit-id") : this.setAttribute("unit-id", String(v)); }

  get name(){ return this.getAttribute("name") || ""; }
  set name(v){ this.setAttribute("name", String(v ?? "")); }

  get size(){ return this.getAttribute("size") || ""; }
  set size(v){ this.setAttribute("size", String(v ?? "")); }

  get dims(){ return this.getAttribute("dims") || ""; }
  set dims(v){ this.setAttribute("dims", String(v ?? "")); }

  get price(){ return this.getAttribute("price") || ""; }
  set price(v){ this.setAttribute("price", String(v ?? "")); }

  get originalPrice(){ return this.getAttribute("original-price") || ""; }
  set originalPrice(v){ this.setAttribute("original-price", String(v ?? "")); }

  get cta(){ return this.getAttribute("cta") || "Select unit"; }
  set cta(v){ this.setAttribute("cta", String(v ?? "")); }

  get href(){ return this.getAttribute("href") || ""; }
  set href(v){ v ? this.setAttribute("href", String(v)) : this.removeAttribute("href"); }

  get selected(){ return this.hasAttribute("selected"); }
  set selected(v){ v ? this.setAttribute("selected","") : this.removeAttribute("selected"); }

  get unavailable(){ return this.hasAttribute("unavailable"); }
  set unavailable(v){ v ? this.setAttribute("unavailable","") : this.removeAttribute("unavailable"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get clickable(){ return this.hasAttribute("clickable"); }
  set clickable(v){ v ? this.setAttribute("clickable","") : this.removeAttribute("clickable"); }

  // ---- render ----
  _sync() {
    const title = clampStr(this.name) || "Unit";
    const size = clampStr(this.size);
    const dims = clampStr(this.dims);

    this._title.textContent = title;

    const bits = [];
    if (size) bits.push(size);
    if (dims) bits.push(dims);
    this._subtitle.textContent = bits.join(" • ");

    const priceText = currency(this.price);
    this._price.textContent = priceText || "—";

    const origText = currency(this.originalPrice);
    if (origText && origText !== priceText) {
      this._orig.hidden = false;
      this._orig.textContent = origText;
    } else {
      this._orig.hidden = true;
      this._orig.textContent = "";
    }

    // button state
    this._selectBtn.textContent = this.unavailable ? "Unavailable" : (this.selected ? "Selected" : this.cta);
    this._selectBtn.toggleAttribute("disabled", this.disabled || this.unavailable);

    // clickable focus
    this._card.setAttribute("tabindex", this.clickable ? "0" : "-1");
  }

  // ---- events ----
  _emitView() {
    // “view details” intent: either open href or emit event for router
    const detail = {
      unitId: this.unitId,
      unit: this._getUnitPayload(),
      href: this.href || null
    };

    this.dispatchEvent(new CustomEvent("ctl-view-unit", {
      bubbles: true,
      composed: true,
      detail
    }));

    // default navigation if href present
    if (this.href) {
      this.dispatchEvent(new CustomEvent("ctl-navigate", {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail
      }));
      // If parent prevents default, do nothing. Otherwise navigate.
      // We can’t directly check preventDefault on CustomEvent after dispatch in all cases,
      // so we keep it conservative: only do hard navigation if not in Storybook.
      // In real app, you’ll intercept ctl-navigate anyway.
    }
  }

  _emitSelect() {
    const detail = {
      unitId: this.unitId,
      unit: this._getUnitPayload()
    };
    this.dispatchEvent(new CustomEvent("ctl-select-unit", {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  _getUnitPayload() {
    return {
      id: this.unitId,
      name: this.name || null,
      size: this.size || null,
      dims: this.dims || null,
      price: this.price || null,
      originalPrice: this.originalPrice || null,
      unavailable: this.unavailable,
      selected: this.selected,
      href: this.href || null
    };
  }

  // ---- handlers ----
  _onSelect(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.disabled || this.unavailable) return;
    this._emitSelect();
  }

  _onClick(e) {
    if (!this.clickable) return;

    // ignore clicks on slotted actions area (buttons/links)
    const path = e.composedPath ? e.composedPath() : [];
    const hitButton = path.some((el) => el && (el.tagName === "CTL-BUTTON" || el.tagName === "BUTTON" || el.tagName === "A" || el.tagName === "CTL-LINK"));
    if (hitButton) return;

    this._emitView();
  }

  _onCardKeydown(e) {
    if (!this.clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this._emitView();
    }
  }
}

customElements.define("ctl-unit-card", CtlUnitCard);
